import { useEffect, useState } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { BrandMark } from './components/BrandMark';
import { Layout } from './components/Layout';
import { Card, Notice } from './components/ui';
import { api } from './lib/api';
import { currentAccount, initialiseAuth, signIn } from './lib/auth';
import { Profile, ProfileProvider } from './lib/profile';
import { Overview } from './pages/Overview';
import { Modules } from './pages/Modules';
import { ModuleDetail } from './pages/ModuleDetail';
import { Applications } from './pages/Applications';
import { ApplicationDetail } from './pages/ApplicationDetail';
import { Roles } from './pages/Roles';
import { RoleDetail } from './pages/RoleDetail';
import { Users } from './pages/Users';
import { UserDetail } from './pages/UserDetail';
import { Audit } from './pages/Audit';
import { Settings } from './pages/Settings';
import { Consents } from './pages/Consents';
import { ConsentGrant } from './pages/ConsentGrant';
import { Approvals } from './pages/Approvals';

type State =
  | { phase: 'loading' }
  | { phase: 'signed-out' }
  | { phase: 'error'; message: string }
  | { phase: 'ready'; profile: Profile };

export function App() {
  const [state, setState] = useState<State>({ phase: 'loading' });

  useEffect(() => {
    void (async () => {
      try {
        await initialiseAuth();
        if (!currentAccount()) {
          setState({ phase: 'signed-out' });
          return;
        }
        const profile = await api.get<Profile>('/api/me');
        setState({ phase: 'ready', profile });
      } catch (error) {
        setState({ phase: 'error', message: error instanceof Error ? error.message : String(error) });
      }
    })();
  }, []);

  // The badge would otherwise go stale the moment somebody else submits.
  useEffect(() => {
    if (state.phase !== 'ready' || !state.profile.isAdmin) return;
    const timer = setInterval(() => {
      void api
        .get<Profile>('/api/me')
        .then((profile) => setState({ phase: 'ready', profile }))
        .catch(() => undefined);
    }, 60_000);
    return () => clearInterval(timer);
  }, [state]);

  if (state.phase === 'loading') {
    return (
      <div className="login">
        <p className="muted">Checking your sign-in…</p>
      </div>
    );
  }

  if (state.phase === 'error') {
    return (
      <div className="login">
        <Card>
          <div className="brand">
            <BrandMark size={26} />
            <h1 className="brand-name">
              Solutec <span className="thin">Data Gateway</span>
            </h1>
          </div>
          <Notice tone="danger" title="Sign-in did not complete">
            {state.message}
          </Notice>
          <button className="primary" onClick={() => window.location.reload()}>
            Try again
          </button>
        </Card>
      </div>
    );
  }

  if (state.phase === 'signed-out') {
    return (
      <div className="login">
        <Card>
          <div className="brand">
            <BrandMark size={26} />
            <h1 className="brand-name">
              Solutec <span className="thin">Data Gateway</span>
            </h1>
          </div>
          <p>Sign in to manage modules, permissions and consents.</p>
          <button className="primary" onClick={() => void signIn()}>
            Sign in with Entra ID
          </button>
        </Card>
      </div>
    );
  }

  const { profile } = state;

  return (
    <ProfileProvider profile={profile}>
      <Layout profile={profile}>
        <Routes>
        <Route path="/consents" element={<Consents />} />
        <Route path="/applications" element={<Applications />} />
        <Route path="/applications/:id" element={<ApplicationDetail />} />
        <Route path="/settings" element={<Settings profile={profile} />} />
        <Route path="/consent" element={<ConsentGrant />} />
        {profile.isAdmin ? (
          <>
            <Route path="/" element={<Overview />} />
            <Route path="/modules" element={<Modules />} />
            <Route path="/modules/:id" element={<ModuleDetail />} />
            <Route path="/roles" element={<Roles />} />
            <Route path="/roles/:id" element={<RoleDetail />} />
            <Route path="/users" element={<Users />} />
            <Route path="/users/:id" element={<UserDetail />} />
            <Route path="/audit" element={<Audit />} />
            <Route path="/approvals" element={<Approvals />} />
          </>
        ) : (
          <Route path="/" element={<Navigate to="/applications" replace />} />
        )}
        <Route
          path="*"
          element={<Navigate to={profile.isAdmin ? '/' : '/applications'} replace />}
        />
        </Routes>
      </Layout>
    </ProfileProvider>
  );
}
